import { faEdit, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    BoxProps,
    IconButton,
    Box as MuiBox,
    Button as MuiButton,
    styled,
    Typography,
} from "@mui/material";
import {
    GridColDef,
    GridRowParams,
    DataGrid as MuiDataGrid,
} from "@mui/x-data-grid";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetUsers } from "src/api/user";
import { DEFAULT_PAGE_SIZE } from "src/components/ListView";
import { SettingContext, SettingContextProvider } from "src/contexts/settings";
import CreateUser from "src/dialogs/CreateUser";
import DeleteDialog from "src/dialogs/DeleteElement";
import { Enum_Entity } from "src/types";
import { Type_User } from "src/types/user";

const DataGrid = styled(MuiDataGrid)(({ theme }) => ({
    width: "98%",
    border: "none",
    marginTop: theme.spacing(2),
    "& .MuiDataGrid-cell": {
        display: "flex",
        alignItems: "center",
    },
}));

const Button = styled(MuiButton)(({ theme }) => ({
    color: theme.palette.secondary.dark,
    borderColor: theme.palette.secondary.dark,
    "&:hover": {
        color: theme.palette.secondary.dark,
        borderColor: theme.palette.secondary.dark,
    },
}));

const Box = styled(MuiBox)<BoxProps>(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginLeft: theme.spacing(3),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
}));

const UserView = () => {
    return (
        <SettingContextProvider>
            <UserSettings />
        </SettingContextProvider>
    );
};

const UserSettings = () => {
    const [openUserManager, setOpenUserManager] = useState(false);
    const [openDeletePopUp, setOpenDeletePopUp] = useState(false);

    const { updateUser } = useContext(SettingContext);

    const token = sessionStorage.getItem("authToken");
    const { data } = useGetUsers(token || "");

    const { t } = useTranslation();

    const handleDelete = (params: GridRowParams) => {
        updateUser(params as unknown as Type_User);
        setOpenDeletePopUp(true);
    };

    const handleEdit = (params: GridRowParams) => {
        updateUser(params as unknown as Type_User);
        setOpenUserManager(true);
    };

    const columns: GridColDef[] = [
        {
            field: "email",
            type: "string",
            flex: 3,
            renderCell: (params) => (
                <Typography>{params.value || ""}</Typography>
            ),
            renderHeader: () => <strong>{t("login.email")}</strong>,
        },
        {
            field: "actions",
            type: "actions",
            flex: 1,
            renderCell: (params) => (
                <>
                    <IconButton
                        size="small"
                        onClick={() => handleEdit(params.row)}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleDelete(params.row)}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                </>
            ),
            renderHeader: () => <strong>{t("actions")}</strong>,
        },
    ];

    return (
        <MuiBox p={2}>
            <Box>
                <Typography variant="h5">{t("user.list")}</Typography>
                <Button
                    variant="outlined"
                    onClick={() => {
                        updateUser(null);
                        setOpenUserManager(true);
                    }}
                >
                    {t("user.create_user")}
                </Button>
            </Box>
            <DataGrid
                rows={data}
                columns={columns}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: DEFAULT_PAGE_SIZE,
                        },
                    },
                }}
                pageSizeOptions={[5, DEFAULT_PAGE_SIZE, 15]}
                disableColumnMenu
            />

            {openUserManager && (
                <CreateUser close={() => setOpenUserManager(false)} />
            )}

            {openDeletePopUp && (
                <DeleteDialog
                    closeDeleteDialog={() => setOpenDeletePopUp(false)}
                    entity={Enum_Entity.USER}
                />
            )}
        </MuiBox>
    );
};

export default UserView;
